import { X } from '@phosphor-icons/react'
import { useEffect, useRef } from 'react'
import type { DiffField, MatchResult } from '../../types'
import {
  DIFF_FIELD_LABELS,
  DIFF_STATUS_LABELS,
  MATCH_TYPE_LABELS,
  formatConfidence,
  formatMoney,
  formatNumber,
  formatPercentage,
} from '../../ui/formatters'

interface DetailDrawerProps {
  onClose: () => void
  result: MatchResult
}

const fieldOrder: readonly DiffField[] = [
  'itemCode',
  'itemName',
  'itemDescription',
  'unit',
  'quantity',
  'unitPrice',
  'totalPrice',
]

function formatValue(field: DiffField, value: string | number | null): string {
  if (typeof value === 'string') return value || '缺失'
  if (field === 'unitPrice' || field === 'totalPrice') return formatMoney(value, '缺失')
  return formatNumber(value, 6, '缺失')
}

export function DetailDrawer({ onClose, result }: DetailDrawerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const restoreFrameRef = useRef<number | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (restoreFrameRef.current !== null) {
      window.cancelAnimationFrame(restoreFrameRef.current)
      restoreFrameRef.current = null
    }
    returnFocusRef.current ??=
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    dialog.showModal()
    dialog.querySelector<HTMLButtonElement>('[data-detail-close]')?.focus()

    return () => {
      if (dialog.open) dialog.close()
      const returnFocus = returnFocusRef.current
      restoreFrameRef.current = window.requestAnimationFrame(() => {
        if (returnFocus?.isConnected) returnFocus.focus()
      })
    }
  }, [])

  return (
    <dialog
      ref={dialogRef}
      className="detail-drawer"
      aria-labelledby="detail-drawer-title"
      aria-describedby="detail-drawer-description"
      aria-modal="true"
      data-testid="detail-drawer"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
    >
      <div className="detail-drawer__header">
        <div>
          <span className={`diff-badge diff-badge--${result.status}`}>
            {DIFF_STATUS_LABELS[result.status]}
          </span>
          <h2 id="detail-drawer-title">
            {result.targetRow?.itemName ?? result.baselineRow?.itemName ?? '未命名项目'}
          </h2>
        </div>
        <button
          className="icon-button"
          data-detail-close
          type="button"
          onClick={onClose}
          aria-label="关闭详情"
        >
          <X aria-hidden="true" />
        </button>
      </div>

      <div className="detail-drawer__meta">
        <div>
          <span>匹配方式</span>
          <strong>{MATCH_TYPE_LABELS[result.matchType]}</strong>
        </div>
        <div>
          <span>可信度</span>
          <strong>{formatConfidence(result.confidence)}</strong>
        </div>
        <div>
          <span>金额影响</span>
          <strong>{formatMoney(result.amountImpact.value)}</strong>
        </div>
      </div>

      <section className="detail-reason" id="detail-drawer-description">
        <h3>匹配说明</h3>
        <p>{result.matchReason}</p>
      </section>

      <section className="field-comparison" aria-label="字段逐项对比">
        <div className="field-comparison__header">
          <span>字段</span>
          <span>基准版</span>
          <span>对比版</span>
          <span>变化</span>
        </div>
        {fieldOrder.map((field) => {
          const change = result.fieldChanges[field]
          return (
            <div
              className={
                change.changed ? 'field-comparison__row is-changed' : 'field-comparison__row'
              }
              key={field}
            >
              <strong>{DIFF_FIELD_LABELS[field]}</strong>
              <span>{formatValue(field, change.baselineValue)}</span>
              <span>{formatValue(field, change.targetValue)}</span>
              <span>
                {change.absoluteChange === null
                  ? change.changed
                    ? '文本变化'
                    : '未变化'
                  : `${formatNumber(change.absoluteChange)}（${formatPercentage(
                      change.percentageChange,
                      change.percentageChangeReason,
                    )}）`}
              </span>
            </div>
          )
        })}
      </section>

      <section className="source-reference">
        <h3>来源定位</h3>
        <dl>
          <div>
            <dt>基准版</dt>
            <dd>
              {result.baselineRow
                ? `${result.baselineRow.fileName} / ${result.baselineRow.sheetName} / 第 ${result.baselineRow.sourceRowNumber} 行`
                : '无对应行'}
            </dd>
          </div>
          <div>
            <dt>对比版</dt>
            <dd>
              {result.targetRow
                ? `${result.targetRow.fileName} / ${result.targetRow.sheetName} / 第 ${result.targetRow.sourceRowNumber} 行`
                : '无对应行'}
            </dd>
          </div>
        </dl>
      </section>
    </dialog>
  )
}
